import { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { createPortal } from 'react-dom'
import { ihm_viewer__dictionary, locale } from 'Application/i18n/i18nViewer'
import { getAccount } from 'Infrastructure/Service/AuthService'
import { CompanyLogo } from 'Ui/Layer/components/information/CompanyLogo'

import 'Application/Templates/Css/Components/Login.css'
import { getRemainingLoginAttempts, getLiveAccess } from 'Infrastructure/Service/AccountService'
import { requestPasswordReset } from 'Infrastructure/Service/PasswordResetService'
import { subscribeToPush } from 'Infrastructure/Service/PushService'

/**
 * Component Login
 * @returns {JSX}
 */
export const Login = ({setConnectionForm}) => {

    const dispatch = useDispatch()
    const logoUrl = `${process.env.REACT_APP_U1_VIEWERSVC_API}company/logo?dark=true`

    const [showStatus, setStatusDisplay] = useState(false)
    const [message, setMessage] = useState("")
    const [remainingAttempts, setRemainingAttempts] = useState(5)
    const [email, setEmail] = useState("")
    const [password, setPassword] = useState("")
    const [showPassword, setShowPassword] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [rememberMe, setRememberMe] = useState(false)

    const close = () => {
        setConnectionForm(false)
        dispatch({ type: "IGNORE_EXPIRED_TOKEN", payload: true })
        dispatch({ type: "CLOSE_LIVE", payload: true })
    }

    const doLogin = async (event) => {
        event?.preventDefault?.()
        if (isSubmitting) return

        setMessage("")
        setIsSubmitting(true)

        try {
            if (rememberMe) {
                localStorage.setItem('viewerRememberedEmail', email.trim())
            } else {
                localStorage.removeItem('viewerRememberedEmail')
            }

            const response = await getAccount(email.trim(), password)
            if (!response.ok) {
                const data = await response.json().catch(() => null)
                const error = new Error('Login failed')
                error.response = response
                error.data = data
                throw error
            }

            const data = await response.json().catch(() => ({}))
            if (data.success || data.token || !data.error) {
                setStatusDisplay({
                    error: false,
                    message: ihm_viewer__dictionary.LOGIN.MESSAGE.OK[locale()],
                })
                dispatch({ type: "USER_CONNECTED", payload: true })
                getLiveAccess()
                    .then(hasAccess => dispatch({ type: 'SET_LIVE_ACCESS', payload: hasAccess }))
                    .catch(() => dispatch({ type: 'SET_LIVE_ACCESS', payload: false }))

                if ('Notification' in window) {
                    if (Notification.permission === 'granted') {
                        subscribeToPush(email.trim());
                    } else if (Notification.permission === 'default') {
                        Notification.requestPermission().then(perm => {
                            if (perm === 'granted') subscribeToPush(email.trim());
                        });
                    }
                }

                setTimeout(() => {
                    close()
                    setStatusDisplay(false)
                }, 900)
                return
            }

            setStatusDisplay({
                error: true,
                message: ihm_viewer__dictionary.LOGIN.MESSAGE.ERROR[locale()],
            })
            if (data.remainingAttempts !== undefined) {
                setRemainingAttempts(data.remainingAttempts)
            }
        } catch (err) {
            let errorMessage = ihm_viewer__dictionary.LOGIN.MESSAGE.ERROR[locale()]

            if (err.response?.status === 429) {
                errorMessage = ihm_viewer__dictionary.LOGINMSG.TOOMANYATTEMPTS[locale()]
                setRemainingAttempts(0)
            } else if (err.data?.remainingAttempts !== undefined) {
                setRemainingAttempts(err.data.remainingAttempts)
            }

            setStatusDisplay({ error: true, message: errorMessage })
            console.error(err)
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleForgotPassword = async () => {
        const trimmed = email.trim()
        if (!trimmed) {
            setMessage(ihm_viewer__dictionary.LOGINMSG.TYPEEMAIL[locale()])
            return
        }

        const result = await requestPasswordReset(trimmed)
        if (result.success) {
            setMessage(ihm_viewer__dictionary.LOGINMSG[result.messageKey][locale()])
        } else {
            setMessage(
                ihm_viewer__dictionary.LOGINMSG[result.messageKey]?.[locale()] ||
                ihm_viewer__dictionary.LOGINMSG.UNKNOWNERROR[locale()]
            )
        }
    }

    useEffect(() => {
        const loadRemainingAttempts = async () => {
            const attempts = await getRemainingLoginAttempts()
            setRemainingAttempts(attempts)
        }
        loadRemainingAttempts()
    }, [])

    useEffect(() => {
        const savedEmail = localStorage.getItem('viewerRememberedEmail')
        if (savedEmail) {
            setEmail(savedEmail)
            setRememberMe(true)
        }
    }, [])

    useEffect(() => {
        // Same behavior as other modals: prevent background scroll
        const prev = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = prev
        }
    }, [])

    const content = (
        <div id="Login" role="dialog" aria-modal="true" aria-labelledby="login-title" onMouseDown={close}>
            <div className="login-backdrop" />

            <div className="login-panel" onMouseDown={(e) => e.stopPropagation()}>
                <header className="login-header">
                    <div className="login-logoWrap">
                        <CompanyLogo url={logoUrl} />
                    </div>
                </header>

                {showStatus && (
                    <div className={`login-status ${showStatus.error ? 'status-error' : 'status-success'}`}>
                        {showStatus.message}
                    </div>
                )}

                <form className="login-form" onSubmit={doLogin}>
                    <div className="login-field">
                        <label htmlFor="login-email">{ihm_viewer__dictionary.LOGIN.EMAIL_ADDRESS[locale()]}</label>
                        <input
                            id="login-email"
                            name="username"
                            type="email"
                            className="login-glassInput"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="email"
                            inputMode="email"
                            required
                        />
                    </div>

                    <div className="login-field">
                        <label htmlFor="login-password">{ihm_viewer__dictionary.LOGIN.PASSWORD[locale()]}</label>
                        <div className="login-passwordRow">
                            <input
                                id="login-password"
                                name="password"
                                type={showPassword ? 'text' : 'password'}
                                className="login-glassInput"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="current-password"
                                required
                            />
                            <span
                                id="toggleDisplayPass"
                                role="button"
                                tabIndex={0}
                                onClick={() => setShowPassword(v => !v)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') setShowPassword(v => !v)
                                }}
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                title={showPassword ? 'Hide password' : 'Show password'}
                            >
                                👁
                            </span>
                        </div>
                    </div>

                    <div className="login-options">
                        <label className="login-remember">
                            <input
                                type="checkbox"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                            />
                            <span>{ihm_viewer__dictionary.LOGIN.REMEMBERME?.[locale()] ?? 'Remember me'}</span>
                        </label>

                        <button type="button" className="login-forgot" onClick={handleForgotPassword}>
                            {ihm_viewer__dictionary.LOGIN.ASK_FORGOTTEN_PASSWORD[locale()]}
                        </button>
                    </div>

                    {(message || (remainingAttempts <= 3 && remainingAttempts > 0) || remainingAttempts === 0) && (
                        <div className="login-hints" role="status">
                            {message && <div className='message'>{message}</div>}
                            {remainingAttempts <= 3 && remainingAttempts > 0 && (
                                <div className='attemptsWarning'>
                                    {remainingAttempts}{' '}
                                    {remainingAttempts === 1
                                        ? ihm_viewer__dictionary.LOGINMSG.ATTEMPTSREMAINING[locale()]
                                        : ihm_viewer__dictionary.LOGINMSG.ATTEMPTSREMAININGPLURAL[locale()]}
                                </div>
                            )}
                        </div>
                    )}

                    <button id="loginBtn" className="login-submit" type="submit" disabled={isSubmitting || remainingAttempts === 0}>
                        {ihm_viewer__dictionary.LOGIN.LOGIN[locale()]}
                    </button>
                </form>

                <footer className="login-footer">
                    <span>
                        {ihm_viewer__dictionary.LOGIN.ASK_FIRST_VISITE[locale()]}{' '}
                        <a href={process.env.REACT_APP_U2_ACCOUNTSVC_PUBLIC_URL + 'account-creation'}>
                            {ihm_viewer__dictionary.LOGIN.CREATE_ACCOUNT[locale()]}
                        </a>
                    </span>
                </footer>
            </div>
        </div>
    )

    return createPortal(content, document.body)
}
